import React from 'react';
import { Select, Form } from 'antd';
import _ from 'lodash';
import { sensorPartNumbers } from '@iso/constants/sensorPartNumbers';
import {
  SENSOR_TYPE_BY_ID,
  ADDITIONAL_PART_NUMBERS_BY_DEVICE_TYPE,
  humanizeSensorTypeName,
} from '@iso/constants/trackpacSensorTypes';

const { Option } = Select;

class BasePartNumberRenderer {
  renderOptions(props = {}) {
    throw new Error('renderOptions method must be implemented');
  }

  renderFormItems(props = {}) {
    throw new Error('renderFormItems method must be implemented');
  }
}

class TrackpacPartNumberRenderer extends BasePartNumberRenderer {
  constructor(deviceList, selectedDevice) {
    super();
    this.deviceList = deviceList;
    this.selectedDevice = selectedDevice;
  }

  renderOptions() {
    const filteredDevices = this.deviceList.filter(device => device.id === this.selectedDevice);
    
    return _.map(filteredDevices, (device, index) => {
      const sensorType = SENSOR_TYPE_BY_ID[device.device_type];
      if (sensorType) {
        return (
          <Option key={index} value={sensorType}>
            <div>{sensorType}</div>
          </Option>
        );
      }
      return null;
    }).filter(Boolean);
  }

  renderFormItems() {
    return (
      <>
        {Array.from({ length: 1 }).map((_, index) => (
          <Form.Item
            key={`DeviceType`}
            name={['sensor', 'part_number', index]}
            label={`Type`}
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
          >
            <Select placeholder="Select a part number" allowClear size="large">
              {this.renderOptions()}
            </Select>
          </Form.Item>
        ))}
      </>
    );
  }
}

class LoraAirAgriPartNumberRenderer extends BasePartNumberRenderer {
  constructor(deviceList, selectedDevice) {
    super();
    this.deviceList = deviceList;
    this.selectedDevice = selectedDevice;
  }

  renderOptions() {
    const filteredDevices = this.deviceList.filter(device => device.id === this.selectedDevice);

    // A single upstream device_type can map to multiple AirAgri part_numbers
    // (e.g. CPL03 → DOOR_CONTACT_SENSOR or DOOR_CONTACT_SENSOR_WATER). The
    // primary mapping in SENSOR_TYPE_BY_ID is the default; variants come from
    // ADDITIONAL_PART_NUMBERS_BY_DEVICE_TYPE. Flatten to one Option per
    // available part_number, labelled with its humanised form.
    const options = [];
    filteredDevices.forEach(device => {
      const primary = SENSOR_TYPE_BY_ID[device.device_type];
      const extras = ADDITIONAL_PART_NUMBERS_BY_DEVICE_TYPE[device.device_type] || [];
      const partNumbers = [primary, ...extras].filter(Boolean);
      partNumbers.forEach(pn => {
        options.push(
          <Option key={`${device.id}-${pn}`} value={pn}>
            <div>{humanizeSensorTypeName(pn)}</div>
          </Option>
        );
      });
    });
    return options;
  }

  renderFormItems() {
    return (
      <>
        {Array.from({ length: 1 }).map((_, index) => (
          <Form.Item
            key={`DeviceType`}
            name={['sensor', 'part_number', index]}
            label={`Type`}
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
          >
            <Select placeholder="Select a part number" allowClear size="large">
              {this.renderOptions()}
            </Select>
          </Form.Item>
        ))}
      </>
    );
  }
}

class TraccarPartNumberRenderer extends BasePartNumberRenderer {
  constructor(deviceList, selectedDevice) {
    super();
    this.deviceList = deviceList;
    this.selectedDevice = selectedDevice;
  }

  renderOptions() {
    // Fixed: Removed undefined variables and provided proper key/value
    return (
      <Option key="asset-tracking" value="ASSET_TRACKING">
        <div>ASSET_TRACKING</div>
      </Option>
    );
  }

  renderFormItems() {
    return (
      <>
        {Array.from({ length: 1 }).map((_, index) => (
          <Form.Item
            key={`TraccarDeviceType`}
            name={['sensor', 'part_number', index]}
            label={`Type`}
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
          >
            <Select placeholder="Select a part number" allowClear size="large">
              {this.renderOptions()}
            </Select>
          </Form.Item>
        ))}
      </>
    );
  }
}

class RFTechnologyPartNumberRenderer extends BasePartNumberRenderer {
  renderOptions() {
    return _.map(sensorPartNumbers, (partNumber, index) => (
      <Option key={index} value={partNumber.value}>
        <div>{partNumber.label}</div>
      </Option>
    ));
  }

  renderFormItems() {
    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <Form.Item
            key={`sensor${index + 1}`}
            name={['sensor', 'part_number', index]}
            label={`Sensor ${index + 1}`}
            rules={[
              {
                required: false,
                message: 'This field is required',
              },
            ]}
          >
            <Select placeholder="Select a part number" allowClear size="large">
              {this.renderOptions()}
            </Select>
          </Form.Item>
        ))}
      </>
    );
  }
}

class KineticSolutionsPartNumberRenderer extends BasePartNumberRenderer {
  constructor(deviceList, selectedDevice) {
    super();
    this.deviceList = deviceList;
    this.selectedDevice = selectedDevice;
  }

  renderOptions() {
    return (
      <Option key="kinetic-weather-station" value="KINETIC_WEATHER_STATION">
        <div>KINETIC_WEATHER_STATION</div>
      </Option>
    );
  }

  renderFormItems() {
    return (
      <>
        {Array.from({ length: 1 }).map((_, index) => (
          <Form.Item
            key={`KineticDeviceType`}
            name={['sensor', 'part_number', index]}
            label={`Type`}
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
          >
            <Select placeholder="Select a part number" allowClear size="large">
              {this.renderOptions()}
            </Select>
          </Form.Item>
        ))}
      </>
    );
  }
}

export class PartNumberRenderFactory {
  static createRenderer(supplier, deviceList = [], selectedDevice = null) {
    if (!supplier) {
      throw new Error('Supplier is required');
    }

    switch (supplier) {
      case 'Trackpac':
        return new TrackpacPartNumberRenderer(deviceList, selectedDevice);
      case 'Lora AirAgri':
        return new LoraAirAgriPartNumberRenderer(deviceList, selectedDevice);
      case 'RF Technology':
        return new RFTechnologyPartNumberRenderer();
      case 'Traccar AirAgri':
        return new TraccarPartNumberRenderer(deviceList, selectedDevice);
      case 'Kinetic Solutions':
        return new KineticSolutionsPartNumberRenderer(deviceList, selectedDevice);
      default:
        throw new Error(`Unsupported supplier for part number rendering: ${supplier}`);
    }
  }
}
